/**
 * 预览页「官网」区块(1009 一期):本文在各官网站点的发布状态 +「发布到官网」入口 + 逐条操作。
 *
 * ★ 改稿不自动覆盖官网:已发布且文章版本更新了 → 提示「官网上是第 N 版,要同步更新吗?」,点了才同步(走 PUT)。
 * ★ 体裁不对(小红书 / 问答)、导出被拦、质检 BLOCKED → 按钮禁用并说清原因(后端同口径再拦一次)。
 * ★ 栏目稿(columnItemId 非空)待审时由上层传 `columnConfirm` 渲染「确认并按计划发布」(二期)。
 */
import { useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, Globe, Loader2 } from "lucide-react";

import { Button } from "@/shared/ui/button";
import { cn } from "@/shared/lib/utils";
import { useConfirm } from "@/shared/hooks/use-confirm";
import { toast } from "@/shared/hooks/use-toast";
import { errorToast } from "@/shared/lib/apiErrors";

import type { ExportPreflight } from "../api/reporterService";
import {
  reporterSiteService,
  type ArticlePublications,
  type SitePublication,
} from "../api/reporterSiteService";
import {
  articlePublicationsKey,
  badgeText,
  canPublishGenre,
  outdatedPrompt,
  SITE_GENRE_BLOCKED_TEXT,
  SITE_STATUS_TONE,
  shortTime,
  siteLabel,
  type SiteTone,
} from "../model/sitePublication";
import { ColumnConfirmBox } from "./ColumnConfirmBox";
import { SitePublishDialog } from "./SitePublishDialog";

const TONE_CLASS: Record<SiteTone, string> = {
  success: "border-success/40 bg-success/10 text-success",
  warning: "border-warning/40 bg-warning/10 text-warning",
  destructive: "border-destructive/40 bg-destructive/10 text-destructive",
  muted: "border-border bg-muted text-muted-foreground",
  primary: "border-primary/40 bg-primary/10 text-primary",
};

export function SiteStatusPill({ p, className }: { p: SitePublication; className?: string }) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border px-1.5 py-px text-2xs",
        TONE_CLASS[SITE_STATUS_TONE[p.status] ?? "muted"],
        className
      )}
      data-site-status={p.status}
    >
      {badgeText(p)}
    </span>
  );
}

export function SitePublicationPanel({
  articleId,
  genre,
  qualityGate,
  preflight,
  defaultSiteId,
  columnConfirm,
  draftId,
}: {
  articleId: number;
  genre: string | null | undefined;
  qualityGate: string | null | undefined;
  preflight: ExportPreflight | undefined;
  defaultSiteId?: string | null;
  columnConfirm?: ReactNode;
  /** 二期:传了就查这篇是不是官网栏目的一期,是则出「确认并按计划发布」。 */
  draftId?: number;
}) {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [open, setOpen] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rescheduleFor, setRescheduleFor] = useState<string | null>(null);
  const [newAt, setNewAt] = useState("");

  const key = articlePublicationsKey(articleId);
  const pubsQ = useQuery({
    queryKey: key,
    queryFn: () => reporterSiteService.byArticle(articleId),
    refetchInterval: (q) =>
      (q.state.data?.items ?? []).some((i) => i.status === "PUBLISHING") ? 3000 : false,
  });
  const items = pubsQ.data?.items ?? [];

  const disabledReason = !canPublishGenre(genre)
    ? SITE_GENRE_BLOCKED_TEXT
    : qualityGate === "BLOCKED"
      ? "质检未通过,改好或重写后才能发到官网"
      : preflight && preflight.blocking.length > 0
        ? "导出前检查有拦截项,修完才能发到官网"
        : !preflight
          ? "导出前检查还没跑完"
          : null;

  const apply = (r: ArticlePublications) => {
    qc.setQueryData(key, r);
    void qc.invalidateQueries({ queryKey: ["reporter-site-badges"] });
  };

  const run = async (
    p: SitePublication,
    fn: () => Promise<ArticlePublications | void>,
    ok: string
  ) => {
    setBusyId(p.id);
    try {
      const r = await fn();
      if (r) apply(r);
      else await qc.invalidateQueries({ queryKey: key });
      const fresh = r?.items.find((i) => i.id === p.id);
      if (fresh?.status === "FAILED" || (fresh?.lastError && fresh.status !== "SCHEDULED")) {
        toast({ title: "没有成功", description: fresh.lastError ?? "", variant: "destructive" });
      } else {
        toast({ title: ok });
      }
    } catch (e) {
      toast(errorToast(e));
    } finally {
      setBusyId(null);
    }
  };

  const confirmFlag = (preflight?.confirmation.length ?? 0) > 0;

  return (
    <div data-site-publication-panel="">
      <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold">
        <Globe className="h-3.5 w-3.5" />
        官网
      </div>
      {items.length > 0 && (
        <ul className="mb-2 space-y-2">
          {items.map((p) => {
            const prompt = outdatedPrompt(p);
            const busy = busyId === p.id;
            return (
              <li
                key={p.id}
                className="rounded-lg border border-border/70 bg-background/50 p-2.5 text-2xs"
                data-site-publication={p.id}
              >
                <div className="flex flex-wrap items-center gap-1.5">
                  <b className="text-xs" data-site-publication-site="">
                    {p.siteName == null ? "站点已删除" : siteLabel(p.siteName, p.siteHomeUrl)}
                  </b>
                  <SiteStatusPill p={p} />
                  {p.status === "PUBLISHED" && p.publishedVersion != null && (
                    <span className="text-muted-foreground">第 {p.publishedVersion} 版</span>
                  )}
                  {p.siteUrl && p.status === "PUBLISHED" && (
                    <a
                      href={p.siteUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-auto inline-flex items-center gap-0.5 text-primary hover:underline"
                    >
                      <ExternalLink className="h-3 w-3" />
                      打开
                    </a>
                  )}
                </div>
                {p.hint && <p className="mt-1 text-warning">{p.hint}</p>}
                {p.lastError && <p className="mt-1 text-destructive">{p.lastError}</p>}
                {prompt && (
                  <p className="mt-1 text-warning" data-site-outdated="">
                    {prompt}
                  </p>
                )}
                {rescheduleFor === p.id && (
                  <div className="mt-1.5 flex items-center gap-1.5">
                    <input
                      type="datetime-local"
                      aria-label="新的发布时间"
                      className="h-7 flex-1 rounded border border-input bg-background px-1.5"
                      value={newAt}
                      onChange={(e) => setNewAt(e.target.value)}
                    />
                    <Button
                      size="sm"
                      className="h-7 text-2xs"
                      disabled={!newAt || busy}
                      onClick={() =>
                        void run(
                          p,
                          () => reporterSiteService.reschedule(p.id, newAt),
                          `已改到 ${shortTime(newAt + ":00")}`
                        ).then(() => setRescheduleFor(null))
                      }
                    >
                      保存
                    </Button>
                  </div>
                )}
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  {p.status === "PUBLISHED" && p.outdated && (
                    <Act
                      onClick={() =>
                        run(p, () => reporterSiteService.push(p.id, confirmFlag), "已同步到最新版")
                      }
                      disabled={busy || !!disabledReason}
                      primary
                    >
                      同步到最新版
                    </Act>
                  )}
                  {(p.status === "SCHEDULED" ||
                    p.status === "FAILED" ||
                    p.status === "CANCELED") && (
                    <Act
                      onClick={() =>
                        run(p, () => reporterSiteService.push(p.id, confirmFlag), "已发布到官网")
                      }
                      disabled={busy || !!disabledReason}
                    >
                      {p.status === "FAILED" ? "重试" : "立即发布"}
                    </Act>
                  )}
                  {(p.status === "SCHEDULED" ||
                    p.status === "FAILED" ||
                    p.status === "CANCELED") && (
                    <Act
                      onClick={() => {
                        setRescheduleFor(rescheduleFor === p.id ? null : p.id);
                        setNewAt("");
                      }}
                      disabled={busy}
                    >
                      {p.status === "CANCELED" ? "重新排期" : "改期"}
                    </Act>
                  )}
                  {(p.status === "SCHEDULED" || p.status === "FAILED") && (
                    <Act
                      onClick={() => run(p, () => reporterSiteService.cancel(p.id), "已取消排期")}
                      disabled={busy}
                    >
                      取消排期
                    </Act>
                  )}
                  {p.status === "UNPUBLISHED" && (
                    <Act
                      onClick={() =>
                        run(p, () => reporterSiteService.push(p.id, confirmFlag), "已重新上线")
                      }
                      disabled={busy || !!disabledReason}
                    >
                      重新上线
                    </Act>
                  )}
                  {p.status === "PUBLISHED" && (
                    <Act
                      onClick={async () => {
                        const ok = await confirm({
                          title: "从官网下线这篇文章?",
                          description: "下线后官网上看不到,文章留在站点草稿里,可随时重新上线。",
                          confirmText: "下线",
                          variant: "warning",
                        });
                        if (ok) await run(p, () => reporterSiteService.unpublish(p.id), "已下线");
                      }}
                      disabled={busy}
                    >
                      下线
                    </Act>
                  )}
                  {p.status !== "PUBLISHING" && (
                    <Act
                      onClick={async () => {
                        const ok = await confirm({
                          title: "删除这条发布记录?",
                          description: p.siteUrl
                            ? "官网上的这篇文章会一起删掉,不能恢复。"
                            : "只删排期,官网上没有这篇文章。",
                          confirmText: "删除",
                          variant: "destructive",
                        });
                        if (ok)
                          await run(
                            p,
                            async () => {
                              await reporterSiteService.remove(p.id);
                            },
                            "已删除"
                          );
                      }}
                      disabled={busy}
                      danger
                    >
                      删除
                    </Act>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {columnConfirm}
      {draftId != null && (
        <ColumnConfirmBox
          draftId={draftId}
          articleId={articleId}
          preflight={preflight}
          blockedReason={disabledReason}
        />
      )}
      <Button
        variant="outline"
        className="w-full"
        disabled={!!disabledReason}
        title={disabledReason ?? "选站点,立即发或定时发"}
        onClick={() => setOpen(true)}
        data-site-publish-open=""
      >
        <Globe className="mr-2 h-4 w-4" />
        发布到官网
      </Button>
      {disabledReason && (
        <p className="mt-1 text-2xs text-muted-foreground" data-site-publish-disabled="">
          {disabledReason}
        </p>
      )}
      {open && (
        <SitePublishDialog
          open={open}
          onOpenChange={setOpen}
          articleId={articleId}
          preflight={preflight}
          defaultSiteId={defaultSiteId}
          onDone={(r, siteId) => {
            apply(r);
            const last = r.items.find((i) => i.siteId === siteId);
            if (last?.status === "FAILED") {
              toast({
                title: "没有发出去",
                description: last.lastError ?? "",
                variant: "destructive",
              });
            } else {
              toast({ title: last?.status === "SCHEDULED" ? "已排期" : "已发布到官网" });
            }
          }}
        />
      )}
    </div>
  );
}

function Act({
  children,
  onClick,
  disabled,
  primary,
  danger,
}: {
  children: ReactNode;
  onClick: () => unknown;
  disabled?: boolean;
  primary?: boolean;
  danger?: boolean;
}) {
  return (
    <Button
      size="sm"
      variant={primary ? "default" : "outline"}
      className={cn("h-6 px-2 text-2xs", danger && "text-destructive hover:text-destructive")}
      disabled={disabled}
      onClick={() => void onClick()}
    >
      {children}
    </Button>
  );
}
